/**
 * PersonalityTab — Configuración de personalidad del agente.
 * Re-skinear estilo Pacientes/SmartFlowsList: navy + chips segmented + cards limpias.
 * Preserva: state, handlers, props (config, chatbotName, onUpdate, onSave, saving, hasChanges).
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Chip,
  Button,
  Slider,
  Collapse,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  IconButton,
} from "@mui/material";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Psychology as PsychologyIcon,
  LocalHospital as LocalHospitalIcon,
  Tune as TuneIcon,
  MedicalServices as MedicalIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  ExpandMore as ExpandIcon,
  AutoAwesome as AutoAwesomeIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";

const TONE_PRESETS = [
  { id: "profesional", label: "Profesional" },
  { id: "amigable",    label: "Amigable" },
  { id: "formal",      label: "Formal" },
  { id: "casual",      label: "Casual" },
  { id: "empatico",    label: "Empático" },
];

const TONE_SLIDERS = [
  { id: "formality",    label: "Formalidad",  leftLabel: "Casual",  rightLabel: "Formal",    defaultValue: 70 },
  { id: "empathy",      label: "Empatía",     leftLabel: "Directo", rightLabel: "Empático",  defaultValue: 80 },
  { id: "length",       label: "Longitud",    leftLabel: "Conciso", rightLabel: "Detallado", defaultValue: 50 },
  { id: "friendliness", label: "Cercanía",    leftLabel: "Serio",   rightLabel: "Amigable",  defaultValue: 60 },
];

const PROMPT_TEMPLATES = {
  medico: {
    label: "Clínica Médica",
    icon: "🏥",
    prompt: `Eres el asistente virtual de una clínica médica. Tu rol es:
- Ayudar a los pacientes a agendar citas
- Responder preguntas sobre servicios y precios
- Orientar sobre síntomas (siempre recomendando consulta médica)
- Ser empático y profesional en todo momento

Nunca des diagnósticos definitivos. Siempre recomienda consultar con un especialista.`,
  },
  dental: {
    label: "Clínica Dental",
    icon: "🦷",
    prompt: `Eres el asistente virtual de una clínica dental. Tu rol es:
- Agendar citas para tratamientos dentales
- Informar sobre procedimientos y costos
- Recordar la importancia de las revisiones periódicas
- Ser amable y tranquilizador con pacientes nerviosos`,
  },
  estetica: {
    label: "Centro de Estética",
    icon: "💆",
    prompt: `Eres el asistente virtual de un centro de estética. Tu rol es:
- Informar sobre tratamientos disponibles
- Ayudar a agendar consultas de valoración
- Responder sobre precios y promociones
- Ser discreto y profesional`,
  },
};

const SPECIALTIES = [
  { id: "general",            label: "Medicina General" },
  { id: "cardiologia",        label: "Cardiología" },
  { id: "dermatologia",       label: "Dermatología" },
  { id: "endocrinologia",     label: "Endocrinología" },
  { id: "gastroenterologia",  label: "Gastroenterología" },
  { id: "ginecologia",        label: "Ginecología y Obstetricia" },
  { id: "neurologia",         label: "Neurología" },
  { id: "oftalmologia",       label: "Oftalmología" },
  { id: "oncologia",          label: "Oncología" },
  { id: "ortopedia",          label: "Ortopedia y Traumatología" },
  { id: "otorrino",           label: "Otorrinolaringología" },
  { id: "pediatria",          label: "Pediatría" },
  { id: "psiquiatria",        label: "Psiquiatría" },
  { id: "urologia",           label: "Urología" },
  { id: "odontologia",        label: "Odontología" },
  { id: "estetica",           label: "Medicina Estética" },
  { id: "nutricion",          label: "Nutrición" },
  { id: "fisioterapia",       label: "Fisioterapia" },
  { id: "otra",               label: "Otra especialidad" },
];

// ─── Estilos compartidos ───
const cardSx = {
  borderRadius: 2.5,
  background: "#fff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
  overflow: "hidden",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#fff",
    fontSize: 13,
    borderRadius: 2,
    "& fieldset":              { borderColor: "#e2e8f0" },
    "&:hover fieldset":        { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset":  { borderColor: "#1e3a5f", borderWidth: 2 },
  },
  "& .MuiInputLabel-root": {
    color: "#64748b",
    fontSize: 13,
    "&.Mui-focused": { color: "#1e3a5f" },
  },
};

// Header colapsable de sección
function SectionHeader({ icon, title, subtitle, expanded, onToggle, right }) {
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 1.25, py: 1.25, px: 1.75, cursor: "pointer",
        "&:hover": { bgcolor: "#f8fafc" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: 1.5,
          background: "#eef2ff", color: "#4f46e5",
          display: "flex", alignItems: "center", justifyContent: "center",
          "& svg": { fontSize: 17 },
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: "#1e3a5f", fontSize: 13.5, fontWeight: 700, letterSpacing: -0.1 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ color: "#64748b", fontSize: 11.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {right}
        <ExpandIcon sx={{
          fontSize: 20, color: "#94a3b8",
          transition: "transform 0.2s",
          transform: expanded ? "rotate(180deg)" : "rotate(0)",
        }}/>
      </Box>
    </Box>
  );
}

export default function PersonalityTab({
  config, chatbotName, onUpdate, onSave, saving, hasChanges,
}) {
  const [identity, setIdentity] = useState({
    specialty:      config?.specialty || "",
    clinic_name:    config?.clinic_name || "",
    clinic_contact: config?.clinic_contact || "",
  });

  const [toneValues, setToneValues] = useState({
    formality:    config?.tone_formality || 70,
    empathy:      config?.tone_empathy || 80,
    length:       config?.tone_length || 50,
    friendliness: config?.tone_friendliness || 60,
  });

  const [selectedPreset, setSelectedPreset] = useState(null);
  const [maxTurns, setMaxTurns] = useState(config?.max_agent_turns || 5);
  const [identityChanged, setIdentityChanged] = useState(false);
  const [expanded, setExpanded] = useState({ identity: true, tone: false, templates: false });

  useEffect(() => {
    if (config) {
      setIdentity({
        specialty:      config.specialty || "",
        clinic_name:    config.clinic_name || "",
        clinic_contact: config.clinic_contact || "",
      });
      setToneValues({
        formality:    config.tone_formality || 70,
        empathy:      config.tone_empathy || 80,
        length:       config.tone_length || 50,
        friendliness: config.tone_friendliness || 60,
      });
      setMaxTurns(config.max_agent_turns || 5);
    }
  }, [config]);

  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  const handleIdentityChange = (field, value) => {
    setIdentity((prev) => ({ ...prev, [field]: value }));
    setIdentityChanged(true);
  };

  const handleToneChange = (sliderId, value) => {
    setToneValues((prev) => ({ ...prev, [sliderId]: value }));
    setSelectedPreset(null);
  };

  const applyPreset = (presetId) => {
    setSelectedPreset(presetId);
    const presets = {
      profesional: { formality: 85, empathy: 70, length: 60, friendliness: 50 },
      amigable:    { formality: 40, empathy: 85, length: 50, friendliness: 90 },
      formal:      { formality: 95, empathy: 60, length: 70, friendliness: 30 },
      casual:      { formality: 25, empathy: 70, length: 40, friendliness: 85 },
      empatico:    { formality: 60, empathy: 95, length: 65, friendliness: 75 },
    };
    if (presets[presetId]) setToneValues(presets[presetId]);
  };

  const applyTemplate = (key) => {
    const t = PROMPT_TEMPLATES[key];
    if (t) onUpdate(t.prompt);
  };

  const promptValue = config?.system_prompt || "";
  const promptLength = promptValue.length;
  const showSaveBar = hasChanges || identityChanged;

  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1fr 280px" },
      gap: 2, alignItems: "start",
    }}>
      {/* COLUMNA PRINCIPAL */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* System Prompt (lo más importante, arriba y sin colapsar) */}
        <Paper sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, gap: 1, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                background: "linear-gradient(135deg, #1e3a5f, #2d5a87)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", boxShadow: "0 2px 8px rgba(30,58,95,0.25)",
                "& svg": { fontSize: 18 },
              }}>
                <PsychologyIcon />
              </Box>
              <Box>
                <Typography sx={{ color: "#1e3a5f", fontSize: 14.5, fontWeight: 700, letterSpacing: -0.1 }}>
                  Personalidad del agente
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: 11.5 }}>
                  Instrucciones principales para la IA · {promptLength.toLocaleString()} caracteres
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title="Copiar al portapapeles">
                <IconButton
                  size="small"
                  onClick={() => promptValue && navigator.clipboard.writeText(promptValue)}
                  sx={{ color: "#94a3b8", "&:hover": { color: "#1e3a5f", bgcolor: "#f1f5f9" } }}
                >
                  <CopyIcon sx={{ fontSize: 16 }}/>
                </IconButton>
              </Tooltip>
              <Tooltip title="Vaciar prompt">
                <IconButton
                  size="small"
                  onClick={() => onUpdate("")}
                  sx={{ color: "#94a3b8", "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" } }}
                >
                  <RefreshIcon sx={{ fontSize: 16 }}/>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <TextField
            fullWidth multiline rows={14}
            value={promptValue}
            onChange={(e) => onUpdate(e.target.value)}
            placeholder={`Eres el asistente virtual de ${chatbotName || "tu clínica"}. Tu rol es...`}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#fafbfc",
                color: "#1e293b",
                fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                fontSize: 13,
                alignItems: "flex-start",
                borderRadius: 2,
                "& fieldset":             { borderColor: "#e2e8f0" },
                "&:hover fieldset":       { borderColor: "#cbd5e1" },
                "&.Mui-focused fieldset": { borderColor: "#1e3a5f", borderWidth: 2 },
              },
            }}
          />

          <Typography sx={{ color: "#94a3b8", fontSize: 11, mt: 1, lineHeight: 1.4 }}>
            Tip: incluye qué hace, qué NO debe hacer, tono esperado y ejemplos cuando sea útil. Las plantillas de la derecha te dan un punto de partida.
          </Typography>
        </Paper>

        {/* Sección "Especialidad y clínica" ocultada del UI.
            Los campos (specialty, clinic_name, clinic_contact) siguen en BD
            y se preservan al guardar — solo no se exponen para edición. */}

        {/* Tono (colapsable) */}
        <Paper sx={cardSx}>
          <SectionHeader
            icon={<TuneIcon />}
            title="Tono de comunicación"
            subtitle="Ajusta cómo se siente el asistente al hablar"
            expanded={expanded.tone}
            onToggle={() => toggle("tone")}
          />
          <Collapse in={expanded.tone} timeout={250}>
            <Box sx={{ p: 1.75, pt: 0 }}>
              {/* Presets como chips segmented (gradient navy en activo) */}
              <Typography sx={{ color: "#64748b", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.85 }}>
                Presets rápidos
              </Typography>
              <Box sx={{ display: "flex", gap: 0.6, mb: 2, flexWrap: "wrap" }}>
                {TONE_PRESETS.map((p) => {
                  const activo = selectedPreset === p.id;
                  return (
                    <Chip
                      key={p.id}
                      label={p.label}
                      size="small"
                      onClick={() => applyPreset(p.id)}
                      sx={{
                        fontSize: 12, fontWeight: 600, height: 28, cursor: "pointer",
                        color: activo ? "#fff" : "#475569",
                        background: activo ? "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)" : "#fff",
                        border: activo ? "none" : "1px solid #e2e8f0",
                        "&:hover": {
                          background: activo
                            ? "linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)"
                            : "#f8fafc",
                        },
                      }}
                    />
                  );
                })}
              </Box>

              {/* Sliders con un solo color (navy) */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {TONE_SLIDERS.map((s) => (
                  <Box key={s.id}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography sx={{ color: "#475569", fontSize: 12.5, fontWeight: 600 }}>
                        {s.label}
                      </Typography>
                      <Typography sx={{ color: "#1e3a5f", fontSize: 12, fontWeight: 700 }}>
                        {toneValues[s.id]}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ color: "#94a3b8", fontSize: 11, minWidth: 50 }}>{s.leftLabel}</Typography>
                      <Slider
                        value={toneValues[s.id]}
                        onChange={(e, v) => handleToneChange(s.id, v)}
                        min={0} max={100}
                        sx={{
                          color: "#1e3a5f", height: 5,
                          "& .MuiSlider-thumb": {
                            width: 14, height: 14, bgcolor: "#1e3a5f", border: "2px solid #fff",
                            boxShadow: "0 2px 6px rgba(30,58,95,0.4)",
                            "&:hover": { boxShadow: "0 0 0 6px rgba(30,58,95,0.12)" },
                          },
                          "& .MuiSlider-rail": { bgcolor: "#e2e8f0" },
                          "& .MuiSlider-track": { border: "none", borderRadius: 3 },
                        }}
                      />
                      <Typography sx={{ color: "#94a3b8", fontSize: 11, minWidth: 50, textAlign: "right" }}>{s.rightLabel}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Preview de instrucciones generadas */}
              <Box sx={{
                mt: 1.5, p: 1.25, borderRadius: 1.5,
                background: "#f1f5f9", border: "1px solid #e2e8f0",
              }}>
                <Typography sx={{
                  color: "#64748b", fontSize: 10.5, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: 0.5, mb: 0.4,
                }}>
                  Resumen del tono
                </Typography>
                <Typography sx={{ color: "#1e3a5f", fontSize: 12, fontStyle: "italic", lineHeight: 1.5 }}>
                  {generateToneInstructions(toneValues)}
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </Paper>

        {/* Save bar inline (la sticky bar global del editor lo cubre también) */}
        {showSaveBar && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small" variant="contained" disabled={saving}
              startIcon={<SaveIcon sx={{ fontSize: 14 }}/>}
              onClick={() => { onSave(identity); setIdentityChanged(false); }}
              sx={{
                background: "linear-gradient(135deg, #1e3a5f, #2d5a87)",
                textTransform: "none", fontWeight: 700, fontSize: 13,
                borderRadius: 2, px: 2.5, py: 0.75,
                boxShadow: "0 4px 12px rgba(30,58,95,0.3)",
                "&:hover": { background: "linear-gradient(135deg, #1e3a5f, #1e4670)" },
              }}
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </Box>
        )}
      </Box>

      {/* COLUMNA DERECHA: Plantillas + Config avanzada */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Paper sx={cardSx}>
          <SectionHeader
            icon={<AutoAwesomeIcon />}
            title="Plantillas de prompt"
            subtitle="Reemplaza el system prompt con un texto base"
            expanded={expanded.templates}
            onToggle={() => toggle("templates")}
          />
          <Collapse in={expanded.templates} timeout={250}>
            <Box sx={{ p: 1.5, pt: 0, display: "flex", flexDirection: "column", gap: 0.6 }}>
              {Object.entries(PROMPT_TEMPLATES).map(([key, t]) => (
                <Box
                  key={key}
                  onClick={() => applyTemplate(key)}
                  sx={{
                    px: 1.25, py: 0.85, borderRadius: 1.5,
                    bgcolor: "#f8fafc", border: "1px solid #e2e8f0",
                    cursor: "pointer", transition: "all 0.15s",
                    display: "flex", alignItems: "center", gap: 1,
                    "&:hover": {
                      bgcolor: "#eef2ff",
                      borderColor: "#c7d2fe",
                      transform: "translateX(2px)",
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 18 }}>{t.icon}</Typography>
                  <Typography sx={{ color: "#475569", fontSize: 12.5, fontWeight: 600 }}>
                    {t.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Paper>

        <Paper sx={{ ...cardSx, p: 1.75 }}>
          <Typography sx={{
            color: "#64748b", fontSize: 10.5, fontWeight: 800,
            textTransform: "uppercase", letterSpacing: 0.6, mb: 1.25,
          }}>
            Configuración
          </Typography>
          <Typography sx={{ color: "#475569", fontSize: 12.5, fontWeight: 600, mb: 0.5 }}>
            Max. turnos antes de escalar
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Slider
              value={maxTurns}
              onChange={(e, v) => setMaxTurns(v)}
              min={1} max={15}
              sx={{
                color: "#1e3a5f", height: 5,
                "& .MuiSlider-thumb": {
                  width: 14, height: 14, bgcolor: "#1e3a5f", border: "2px solid #fff",
                  boxShadow: "0 2px 6px rgba(30,58,95,0.4)",
                },
                "& .MuiSlider-rail":  { bgcolor: "#e2e8f0" },
                "& .MuiSlider-track": { border: "none" },
              }}
            />
            <Typography sx={{ color: "#1e3a5f", fontSize: 14, fontWeight: 800, minWidth: 20, textAlign: "right" }}>
              {maxTurns}
            </Typography>
          </Box>
          <Typography sx={{ color: "#94a3b8", fontSize: 11, mt: 0.5, lineHeight: 1.4 }}>
            Intercambios antes de sugerir hablar con humano
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}

function generateToneInstructions(toneValues) {
  const instructions = [];
  if (toneValues.formality > 70)       instructions.push("Usa un tono profesional y formal");
  else if (toneValues.formality < 40)  instructions.push("Usa un tono casual y relajado");
  if (toneValues.empathy > 70)         instructions.push("Muestra empatía y comprensión");
  if (toneValues.length > 70)          instructions.push("Da respuestas detalladas y completas");
  else if (toneValues.length < 40)     instructions.push("Sé conciso y directo");
  if (toneValues.friendliness > 70)    instructions.push("Sé amigable y cercano");
  return instructions.length ? instructions.join(". ") + "." : "Tono balanceado.";
}
